import type React from "react";
import { useState } from "react";

import { Stack } from "@mui/material";
import { FormProvider, useForm } from "react-hook-form";
import { useTranslation } from "react-i18next";
import { useParams } from "react-router-dom";

import { usePostMedicalNoteAddendum } from "api/hooks/usePostMedicalNoteAddendum";
import { BaseButton } from "shared/atoms/BaseButton";
import { MUITextInput } from "shared/atoms/inputs";
import { Notification } from "shared/atoms/Notification";

interface Props {
  careEventId: number;
  setFormTriggerHeader: (formTriggerHeader: boolean) => void;
}

export const AddendumForm: React.FC<Props> = ({ careEventId, setFormTriggerHeader }) => {
  const [showAddendumForm, setShowAddendumForm] = useState(false);
  const [error, setError] = useState(false);
  const patientId = Number(useParams().patientId);
  const { t } = useTranslation();
  const form = useForm({
    mode: "onChange",
  });

  const {
    formState: { errors },
    handleSubmit,
    reset,
  } = form;

  const postMedicalNoteAddendumMutation = usePostMedicalNoteAddendum();

  const onSubmit = handleSubmit(({ addendum }) => {
    postMedicalNoteAddendumMutation.mutate(
      { body: addendum, careEventId, patientId },
      {
        onSuccess() {
          setShowAddendumForm(false);
          setError(false);
          reset();
        },
        onError() {
          setError(true);
        },
      }
    );
  });

  return (
    <>
      {!showAddendumForm && (
        <BaseButton
          data-testid="write-addendum-button"
          onClick={() => {
            setShowAddendumForm(true);
            setFormTriggerHeader(true);
          }}
          variant="text"
          text={t("patients.notes.sections.addenda.write_addendum")}
          icon="pen-to-square"
          style={{ minHeight: "auto", padding: 0 }}
          size="large"
        />
      )}

      {showAddendumForm && (
        <>
          <FormProvider {...form}>
            <form onSubmit={onSubmit}>
              <MUITextInput
                error={errors?.addendum ? t("errors.string.empty") : undefined}
                label={t("patients.notes.sections.addenda.label")}
                name="addendum"
                type="text"
                multiline
                required
              />

              <Stack direction="row" justifyContent="flex-start" spacing={2} my={2}>
                <BaseButton
                  text={t("buttons.cancel")}
                  onClick={() => {
                    setShowAddendumForm(false);
                    setFormTriggerHeader(false);
                  }}
                  variant="tertiary"
                  size="large"
                  style={{ flexBasis: 1 }}
                />
                <BaseButton text={t("buttons.sign")} type="submit" size="large" style={{ flexBasis: 1 }} />
              </Stack>
            </form>
          </FormProvider>

          {error && <Notification variant="danger">{t("patients.notes.sections.addenda.error")}</Notification>}
        </>
      )}
    </>
  );
};
